/**
 * Format a date string to Australian format (dd/MM/yyyy)
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateString;
  }
};

/**
 * Convert a Date object to a LOCAL yyyy-MM-dd ISO string (no timezone shift).
 *
 * Jul 2026 — CRITICAL: DO NOT use `d.toISOString().split('T')[0]` for date-only
 * fields. `.toISOString()` returns UTC, so at 09:42 Sydney AEST (UTC+10) on
 * 21 Jul it returns "2026-07-20". Every form default that used the naive UTC
 * pattern was showing yesterday's date to Sydney users. Use this helper
 * whenever the value is populating a <input type="date"> or a
 * `payment_date` / `purchase_date` / `date_in` / etc. field that is meant
 * to be the user's local calendar day.
 *
 * @param {Date} d - Date object (defaults to now)
 * @returns {string} yyyy-MM-dd
 */
export const dateToLocalISO = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Shortcut for `dateToLocalISO(new Date())` — the local calendar day right now. */
export const todayLocalISO = () => dateToLocalISO(new Date());

/**
 * Format a date string with time
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date string with time
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-AU', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};
